import type { SVGProps, ReactNode } from "react";
import { cn } from "../../../lib/cn";

/**
 * Ornements Etheria — jeu de motifs SVG inline (aucune image, ~0 poids) qui
 * matérialisent la DA médiéval + steampunk : filigranes d'angle, rouages,
 * sceaux de cire, divisieurs gravés. Purement décoratifs (`aria-hidden`).
 *
 * Tous consomment `currentColor` → héritent de la couleur du parent (or par défaut
 * via text-gold-*). Poser `className` pour la taille/teinte.
 */

type Orn = SVGProps<SVGSVGElement>;

/** Dégradé laiton partagé (défini une fois, réutilisé par id). */
export function OrnamentDefs() {
  return (
    <svg width="0" height="0" className="absolute" aria-hidden focusable="false">
      <defs>
        <linearGradient id="orn-brass" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f6e2a8" />
          <stop offset="0.5" stopColor="#e5b64b" />
          <stop offset="1" stopColor="#8f5d2b" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * FILIGRANE D'ANGLE — volute de laiton gravée. `corner` oriente le motif.
 * ------------------------------------------------------------------ */
export type Corner = "tl" | "tr" | "bl" | "br";

const CORNER_ROTATION: Record<Corner, string> = {
  tl: "rotate(0)",
  tr: "scale(-1,1)",
  bl: "scale(1,-1)",
  br: "scale(-1,-1)",
};

export function FiligreeCorner({
  corner = "tl",
  className,
  ...rest
}: Orn & { corner?: Corner }) {
  return (
    <svg
      viewBox="0 0 64 64"
      className={cn("pointer-events-none text-gold-400/50", className)}
      aria-hidden
      focusable="false"
      {...rest}
    >
      <g
        transform={`translate(32,32) ${CORNER_ROTATION[corner]} translate(-32,-32)`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      >
        <path d="M4 30 C4 14 14 4 30 4" opacity="0.9" />
        <path d="M4 22 C4 12 12 4 22 4" opacity="0.55" />
        <path d="M10 30 C10 30 12 18 24 14 C30 12 30 20 24 22 C18 24 16 30 22 34" opacity="0.85" />
        <circle cx="10" cy="10" r="2.2" fill="currentColor" stroke="none" opacity="0.9" />
        <path d="M30 4 c6 0 10 2 12 6" opacity="0.4" />
      </g>
    </svg>
  );
}

/** Pose les 4 filigranes d'angle dans un conteneur relatif. */
export function FiligreeFrame({ className, size = 40 }: { className?: string; size?: number }) {
  const s = { width: size, height: size };
  return (
    <div aria-hidden className={cn("pointer-events-none absolute inset-0", className)}>
      <FiligreeCorner corner="tl" style={s} className="absolute left-1.5 top-1.5" />
      <FiligreeCorner corner="tr" style={s} className="absolute right-1.5 top-1.5" />
      <FiligreeCorner corner="bl" style={s} className="absolute bottom-1.5 left-1.5" />
      <FiligreeCorner corner="br" style={s} className="absolute bottom-1.5 right-1.5" />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * ROUAGE — engrenage à N dents. Décor mécanique (spin optionnel via classe).
 * ------------------------------------------------------------------ */
export function Gear({
  teeth = 10,
  className,
  ...rest
}: Orn & { teeth?: number }) {
  const cx = 32;
  const cy = 32;
  const rOuter = 28;
  const rInner = 22;
  const rHub = 9;
  const step = (Math.PI * 2) / teeth;
  const pts: string[] = [];
  for (let i = 0; i < teeth; i++) {
    const a0 = i * step;
    const a1 = a0 + step * 0.28;
    const a2 = a0 + step * 0.5;
    const a3 = a0 + step * 0.78;
    for (const [r, a] of [
      [rOuter, a0],
      [rOuter, a1],
      [rInner, a2],
      [rInner, a3],
    ] as const) {
      pts.push(`${(cx + Math.cos(a) * r).toFixed(2)},${(cy + Math.sin(a) * r).toFixed(2)}`);
    }
  }
  return (
    <svg
      viewBox="0 0 64 64"
      className={cn("text-bronze-400/40", className)}
      aria-hidden
      focusable="false"
      {...rest}
    >
      <polygon
        points={pts.join(" ")}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx={cx} cy={cy} r={rHub} fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle cx={cx} cy={cy} r="2.4" fill="currentColor" />
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * SCEAU DE CIRE — médaillon estampillé (badge de rang / accent).
 * `children` = glyphe central (lettre, emoji, icône).
 * ------------------------------------------------------------------ */
export function WaxSeal({
  children,
  className,
  tone = "gold",
}: {
  children?: ReactNode;
  className?: string;
  tone?: "gold" | "blood" | "arcane" | "violet" | "bronze";
}) {
  const TONE: Record<string, { from: string; to: string; ring: string }> = {
    gold: { from: "#efce76", to: "#a17722", ring: "rgba(229,182,75,0.6)" },
    bronze: { from: "#d3a06b", to: "#6e4520", ring: "rgba(166,110,54,0.6)" },
    blood: { from: "#c53c4d", to: "#66101a", ring: "rgba(178,30,47,0.6)" },
    arcane: { from: "#54cbbb", to: "#177065", ring: "rgba(47,191,174,0.6)" },
    violet: { from: "#a884f8", to: "#3f2088", ring: "rgba(139,92,246,0.6)" },
  };
  const t = TONE[tone] ?? TONE.gold!;
  return (
    <span
      className={cn(
        "relative inline-flex items-center justify-center rounded-full font-display text-sm font-bold text-obsidian-600",
        className,
      )}
      style={{
        width: "2.25rem",
        height: "2.25rem",
        background: `radial-gradient(circle at 35% 28%, ${t.from}, ${t.to})`,
        boxShadow: `0 0 0 1px ${t.ring}, inset 0 2px 4px rgba(255,255,255,0.35), inset 0 -3px 6px rgba(0,0,0,0.45), 0 3px 8px -2px rgba(0,0,0,0.6)`,
        clipPath:
          "polygon(50% 0%, 63% 8%, 79% 6%, 88% 20%, 100% 30%, 95% 47%, 100% 63%, 87% 74%, 84% 91%, 67% 92%, 50% 100%, 33% 92%, 16% 91%, 13% 74%, 0% 63%, 5% 47%, 0% 30%, 12% 20%, 21% 6%, 37% 8%)",
      }}
      aria-hidden
    >
      <span className="drop-shadow-[0_1px_0_rgba(255,255,255,0.25)]">{children}</span>
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * DIVISEUR GRAVÉ — filet + losange central serti (séparateur de section).
 * ------------------------------------------------------------------ */
export function EngravedDivider({
  className,
  label,
}: {
  className?: string;
  label?: ReactNode;
}) {
  return (
    <div className={cn("flex items-center gap-3 text-gold-400/70", className)} aria-hidden>
      <span className="h-px flex-1 bg-gradient-to-r from-transparent to-gold-400/40" />
      {label ? (
        <span className="font-display text-xs uppercase tracking-seal text-gold-200/70">{label}</span>
      ) : (
        <DiamondNode />
      )}
      <span className="h-px flex-1 bg-gradient-to-l from-transparent to-gold-400/40" />
    </div>
  );
}

function DiamondNode() {
  return (
    <svg viewBox="0 0 24 12" className="h-3 w-6" aria-hidden focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.2">
        <path d="M12 1 L17 6 L12 11 L7 6 Z" />
        <path d="M12 4 L14.5 6 L12 8 L9.5 6 Z" fill="currentColor" opacity="0.5" />
      </g>
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * BANDEAU FILIGRANÉ — ruban horizontal ornementé pour en-têtes de panneau.
 * ------------------------------------------------------------------ */
export function FiligreeRule({ className, ...rest }: Orn) {
  return (
    <svg
      viewBox="0 0 240 12"
      preserveAspectRatio="none"
      className={cn("h-3 w-full text-gold-400/45", className)}
      aria-hidden
      focusable="false"
      {...rest}
    >
      <g fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round">
        <path d="M0 6 H96" />
        <path d="M144 6 H240" />
        <path d="M108 6 c3 -4 9 -4 12 0 c3 4 9 4 12 0" />
        <circle cx="120" cy="6" r="2.4" fill="currentColor" stroke="none" />
        <path d="M96 6 c2 -3 6 -3 8 0" opacity="0.6" />
        <path d="M136 6 c2 3 6 3 8 0" opacity="0.6" />
      </g>
    </svg>
  );
}
